import { useState, useEffect, useCallback } from 'react'
import { Sound } from '../types/Sound'
import { soundManager } from '../services/soundManager'

export const useSoundManager = () => {
  const [sounds, setSounds] = useState<Sound[]>([])
  const [loading, setLoading] = useState(true)

  // Load sounds on mount
  useEffect(() => {
    const loadSounds = () => {
      try {
        const userSounds = soundManager.getSounds()
        setSounds(userSounds)
        setLoading(false)
      } catch (error) {
        console.error('Failed to load sounds:', error)
        setLoading(false)
      }
    }

    loadSounds()
  }, [])

  // Add a new sound
  const addSound = useCallback(async (blob: Blob, label: string, category: string = 'custom'): Promise<Sound> => {
    try {
      const newSound = await soundManager.addSound(blob, label, category)
      setSounds(prev => [...prev, newSound])
      return newSound
    } catch (error) {
      console.error('Failed to add sound:', error)
      throw error
    }
  }, [])

  // Remove a sound
  const removeSound = useCallback((soundId: string): boolean => {
    try {
      const success = soundManager.removeSound(soundId)
      if (success) {
        setSounds(prev => prev.filter(sound => sound.id !== soundId))
      }
      return success
    } catch (error) {
      console.error('Failed to remove sound:', error)
      return false
    }
  }, [])

  // Update sound label
  const updateSoundLabel = useCallback((soundId: string, newLabel: string): boolean => {
    try {
      const success = soundManager.updateSoundLabel(soundId, newLabel)
      if (success) {
        setSounds(prev => prev.map(sound =>
          sound.id === soundId ? { ...sound, label: newLabel.trim() } : sound
        ))
      }
      return success
    } catch (error) {
      console.error('Failed to update sound label:', error)
      return false
    }
  }, [])

  // Get sounds by category
  const getSoundsByCategory = useCallback((category: string): Sound[] => {
    return sounds.filter(sound => sound.category === category)
  }, [sounds])

  // Get all categories
  const getCategories = useCallback((): string[] => {
    return soundManager.getCategories()
  }, [])

  // Get sound count
  const getSoundCount = useCallback((): number => {
    return sounds.length
  }, [sounds])

  // Clear all sounds
  const clearAllSounds = useCallback((): void => {
    try {
      soundManager.clearAllSounds()
      setSounds([])
    } catch (error) {
      console.error('Failed to clear sounds:', error)
    }
  }, [])

  // Export sounds
  const exportSounds = useCallback((): string => {
    return soundManager.exportSounds()
  }, [])

  // Import sounds
  const importSounds = useCallback((jsonData: string): boolean => {
    try {
      const success = soundManager.importSounds(jsonData)
      if (success) {
        const userSounds = soundManager.getSounds()
        setSounds(userSounds)
      }
      return success
    } catch (error) {
      console.error('Failed to import sounds:', error)
      return false
    }
  }, [])

  return {
    sounds,
    loading,
    addSound,
    removeSound,
    updateSoundLabel,
    getSoundsByCategory,
    getCategories,
    getSoundCount,
    clearAllSounds,
    exportSounds,
    importSounds
  }
}
